import React from 'react';

/**
 * Canonical "front door" for a user with no permitted apps yet. The access
 * catalog is the request-access entry point; the route guard redirects if the
 * catalog itself is not (yet) permitted, so this is always a safe destination.
 */
export const CATALOG_ROUTE = '/access-catalog';

/**
 * Cold-start state for a net-new user/tenant with zero permitted apps. Rather
 * than a blank grid, Home leads with the catalog front door and a search hint.
 * Opaque token surfaces, ≥44px targets, keyboard-focusable, reduced-motion safe.
 */
export function ColdStartEmptyState({ onBrowse }: { onBrowse: () => void }) {
  return (
    <div
      data-testid="home-coldstart"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 12,
        padding: '40px 24px',
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border, var(--input-border))',
        borderRadius: 'var(--card-radius, 12px)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--main-text)',
          letterSpacing: '-0.01em',
        }}
      >
        No apps assigned yet
      </h3>
      <p
        style={{
          margin: 0,
          maxWidth: 460,
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--main-text-secondary)',
        }}
      >
        Browse the catalog to request access to the apps and resources you need.
        Once access is granted, your apps appear here automatically.
      </p>
      <button
        type="button"
        data-testid="home-coldstart-browse"
        onClick={onBrowse}
        style={{
          marginTop: 4,
          minHeight: 44,
          padding: '0 20px',
          borderRadius: 'var(--button-radius, 8px)',
          border: 'none',
          background: 'var(--button-primary-bg, var(--quantum-violet, var(--main-accent)))',
          color: 'var(--color-on-accent, #ffffff)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Browse the catalog
      </button>
      <p
        style={{
          margin: 0,
          fontSize: 12,
          color: 'var(--main-text-secondary)',
        }}
      >
        or press <kbd>Ctrl</kbd>+<kbd>K</kbd> to search everything
      </p>
    </div>
  );
}

export default ColdStartEmptyState;
